import FetchingSelect from '@/components/FetchingSelect';
import { ActivityReport } from '@/models/activity-report.model';
import { BaseErrorModel } from '@/models/core.model';
import { RunningClubEvent, RunningClubEventList, ClubEventStatusList, UpsertRunningClubEvent } from '@/models/running-club-event.model';
import { getPagedRunningClubEvents, getRunningClubEvent, upsertRunningClubEvent } from '@/services/running-club-event.service';
import { getRunningClubsLookupByLocation } from '@/services/running-club.service';
import { utc_iana_time_zone_id } from '@/constants/time';
import { jsDateToZone } from '@/utils/date-time';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, Form, Loader, Message, Modal, Radio, RadioGroup, Stack, Tag } from 'rsuite';

// Events whose start is within this window of the activity start count as "at that time".
const EVENT_WINDOW_MS = 24 * 60 * 60 * 1000;

interface AssignClubEventDialogProps {
  open: boolean;
  onClose: () => void;
  report: ActivityReport;
}

/** GeoJSON LineString ([lng, lat] order) built from the activity route, as the event's jsonPath expects. */
const routeToGeoJson = (report: ActivityReport): string | undefined => {
  const route = report.activity?.route;
  if (!route || route.length < 2) return undefined;
  return JSON.stringify({
    type: 'LineString',
    coordinates: route.map(p => [p.lng, p.lat]),
  });
};

const AssignClubEventDialog: React.FC<AssignClubEventDialogProps> = ({ open, onClose, report }) => {
  const nav = useNavigate();
  const activity = report.activity;
  const cityId = activity?.cityId ?? activity?.city?.id;
  const countryId = cityId ? undefined : (activity?.countryId ?? activity?.country?.id);
  const zone = activity?.city?.ianaTimeZoneId ?? utc_iana_time_zone_id;
  const jsonPath = routeToGeoJson(report);

  const [clubId, setClubId] = useState<number | undefined>();
  const [events, setEvents] = useState<RunningClubEventList[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [eventId, setEventId] = useState<number | undefined>();
  const [saving, setSaving] = useState(false);
  const [assignedEventId, setAssignedEventId] = useState<number | undefined>();

  useEffect(() => {
    setEventId(undefined);
    setEvents([]);
    if (!clubId) return;
    setLoadingEvents(true);
    getPagedRunningClubEvents({
      pageNumber: 1,
      pageSize: 100,
      orderBy: 'StartTime',
      isAscending: false,
      runningClubId: clubId,
    })
      .then(data => setEvents(data.result))
      .catch(err => {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(errorModel ?? 'Error in loading club events.');
      })
      .finally(() => setLoadingEvents(false));
  }, [clubId]);

  const activityStart = activity?.startTime?.getTime();
  const distance = (e: RunningClubEventList) => Math.abs(e.startTime.getTime() - (activityStart ?? 0));
  const visibleEvents = (showAll || activityStart == null
    ? events
    : events.filter(e => distance(e) <= EVENT_WINDOW_MS)
  ).slice().sort((a, b) => distance(a) - distance(b));

  const handleAssign = async () => {
    if (!eventId || !jsonPath) return;
    setSaving(true);
    try {
      const current = await getRunningClubEvent(eventId);
      const ev: RunningClubEvent = current.result;
      if (ev.jsonPath) {
        setEvents(pre => pre.map(e => e.id === ev.id ? { ...e, hasRoute: true } : e));
        setEventId(undefined);
        toastErrorMessage(`"${ev.name}" already has a route.`);
        return;
      }
      const upsert: UpsertRunningClubEvent = {
        id: ev.id,
        name: ev.name,
        description: ev.description,
        coverImage: ev.coverImage,
        startTime: ev.startTime,
        endTime: ev.endTime,
        startLat: ev.startLat,
        startLng: ev.startLng,
        endLat: ev.endLat,
        endLng: ev.endLng,
        startAddress: ev.startAddress,
        jsonPath,
        visibility: ev.visibility,
        status: ev.status,
        customRoleIds: ev.customRoleIds ?? [],
        runningClubId: ev.runningClubId,
      };
      const result = await upsertRunningClubEvent(upsert);
      if (result.success) {
        toastSuccessMessage('Route assigned to the club event.');
        setAssignedEventId(ev.id);
        setEvents(pre => pre.map(e => e.id === ev.id ? { ...e, hasRoute: true } : e));
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(errorModel ?? 'Error in assigning the route.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="md">
      <Modal.Header>
        <Modal.Title>Assign Route to Run Club Event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form fluid>
          <Form.Group>
            <Form.Label>User Message</Form.Label>
            <Message bordered style={{ whiteSpace: 'pre-wrap' }}>{report.description || '—'}</Message>
          </Form.Group>

          {!jsonPath && <Message type="warning" showIcon>This activity has no route to assign.</Message>}

          <Form.Group>
            <Form.Label>
              Club {cityId ? `(city: ${activity?.city?.name ?? cityId})` : countryId ? `(country: ${activity?.country?.name ?? countryId})` : '(all)'}
            </Form.Label>
            <FetchingSelect
              block
              placeholder="Search club..."
              value={clubId}
              onChange={v => { setClubId(v ?? undefined); setAssignedEventId(undefined); }}
              fetchData={(keyword, page) => getRunningClubsLookupByLocation(keyword, page, cityId, countryId)}
            />
          </Form.Group>

          {clubId && (
            <Form.Group>
              <Stack justifyContent="space-between">
                <Form.Label>
                  Events {showAll ? '' : `around ${jsDateToZone(activity?.startTime, zone)} (±24h)`}
                </Form.Label>
                <Checkbox checked={showAll} onChange={(_, c) => setShowAll(c)}>Show all events</Checkbox>
              </Stack>
              {loadingEvents ? <Loader /> : visibleEvents.length === 0 ? (
                <Message type="info">No events found{showAll ? '' : ' at this time. Try "Show all events"'}.</Message>
              ) : (
                <RadioGroup value={eventId} onChange={v => { setEventId(v as number); setAssignedEventId(undefined); }}>
                  {visibleEvents.map(e => (
                    <Radio key={e.id} value={e.id} disabled={e.hasRoute && e.id !== assignedEventId}>
                      <strong>{e.name}</strong>{' '}
                      — {jsDateToZone(e.startTime, e.ianaTimeZoneId ?? zone)}{' '}
                      <Tag size="sm">{ClubEventStatusList.find(s => s.value == e.status)?.label}</Tag>
                      {e.hasRoute && <Tag size="sm" color="orange">Already has route</Tag>}
                    </Radio>
                  ))}
                </RadioGroup>
              )}
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {assignedEventId && (
          <Button appearance="ghost" onClick={() => nav(`/running-clubs/events/${assignedEventId}`)}>
            Go To Event
          </Button>
        )}
        <Button appearance="primary" onClick={handleAssign} loading={saving} disabled={!eventId || !jsonPath}>
          Assign Path
        </Button>
        <Button appearance="subtle" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignClubEventDialog;
